import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package, MessageSquare, Trash2, Plus, Save, X, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, AVAILABILITY } from "@/data/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["New", "Under Review", "Quoted", "Closed"];
const EMPTY_PRODUCT = { slug: "", name: "", category: CATEGORIES[0], short_description: "", long_description: "", origin: "India", packaging: "", availability: "Regular Supply", image_url: "", variety: "", moq: "", supply_capacity: "", shipment_options: "", featured: false };

export default function AdminDashboardPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_PRODUCT);

  useEffect(() => { document.title = "Admin Dashboard — Aaha Kanakam Traders"; }, []);
  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [loading, user, navigate]);

  const loadEnquiries = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/admin/enquiries", { params }).then(({ data }) => setEnquiries(data)).catch(() => {});
  };
  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});

  useEffect(() => { if (user) { loadEnquiries(); loadProducts(); } }, [user]);
  useEffect(() => { if (user) loadEnquiries(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/enquiries/${id}`, { status }); toast.success("Status updated"); loadEnquiries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/admin/enquiries/${id}`); toast.success("Enquiry deleted"); loadEnquiries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveProduct = async () => {
    try {
      if (creating) {
        await api.post("/admin/products", form);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing}`, form);
        toast.success("Product updated");
      }
      setEditing(null); setCreating(false); setForm(EMPTY_PRODUCT); loadProducts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteProduct = async (slug) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${slug}`); toast.success("Product deleted"); loadProducts(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (p) => { setCreating(false); setEditing(p.slug); setForm({ ...EMPTY_PRODUCT, ...p }); };
  const openCreate = () => { setCreating(true); setEditing("new"); setForm(EMPTY_PRODUCT); };

  const exportCSV = () => {
    const headers = ["Created", "Name", "Company", "Email", "Phone", "Country", "Category", "Product", "Quantity", "Unit", "Destination", "Status"];
    const rows = enquiries.map((e) => [e.created_at, e.full_name, e.company_name, e.email, e.phone, e.country, e.commodity_category, e.product, e.quantity, e.unit, e.destination_country, e.status].map((v) => `"${(v || "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "enquiries.csv"; a.click();
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-slateneutral">Loading…</div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-surface">
      <header className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl">Aaha Kanakam · <span className="text-gold">Admin</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/70 hidden sm:block">{user.email}</span>
            <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-xs uppercase tracking-widest text-white/80 hover:text-gold flex items-center gap-1.5"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex gap-2 border-b border-borderSubtle mb-6">
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} icon={MessageSquare} label={`Enquiries (${enquiries.length})`} tid="tab-enquiries" />
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} icon={Package} label={`Products (${products.length})`} tid="tab-products" />
        </div>

        {tab === "enquiries" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setStatusFilter("")} className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${statusFilter === "" ? "bg-forest text-white border-forest" : "bg-white border-borderSubtle"}`}>All</button>
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${statusFilter === s ? "bg-forest text-white border-forest" : "bg-white border-borderSubtle"}`}>{s}</button>
                ))}
              </div>
              <button onClick={exportCSV} className="btn-gold px-4 py-2 text-xs uppercase tracking-widest font-semibold">Export CSV</button>
            </div>
            <div className="bg-white border border-borderSubtle overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface text-charcoal">
                  <tr>
                    {["Date", "Contact", "Product", "Qty", "Destination", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slateneutral">No enquiries yet.</td></tr>}
                  {enquiries.map((e, i) => (
                    <tr key={e.id} data-testid={`admin-inquiry-row-${i}`} className="border-t border-borderSubtle align-top">
                      <td className="px-4 py-3 text-xs text-slateneutral whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-forest">{e.full_name}</div>
                        <div className="text-xs text-slateneutral">{e.company_name}</div>
                        <div className="text-xs text-slateneutral">{e.email} · {e.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{e.product || "—"}</div>
                        <div className="text-xs text-slateneutral">{e.commodity_category}</div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{e.quantity} {e.unit}</td>
                      <td className="px-4 py-3 text-sm">{e.destination_country} {e.destination_port && `· ${e.destination_port}`}</td>
                      <td className="px-4 py-3">
                        <select value={e.status} onChange={(ev) => updateStatus(e.id, ev.target.value)} className="text-xs border border-borderSubtle px-2 py-1 bg-white">
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3"><button onClick={() => deleteEnquiry(e.id)} className="text-red-600 hover:text-red-800"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="text-xs text-slateneutral">Manage the commodity catalogue.</div>
              <button data-testid="admin-add-product" onClick={openCreate} className="btn-gold px-4 py-2 text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-1.5"><Plus size={14} /> Add Product</button>
            </div>
            <div className="bg-white border border-borderSubtle overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface"><tr>{["Name", "Category", "Availability", "Featured", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-borderSubtle">
                      <td className="px-4 py-3"><div className="font-medium text-forest">{p.name}</div><div className="text-xs text-slateneutral">{p.slug}</div></td>
                      <td className="px-4 py-3 text-sm">{p.category}</td>
                      <td className="px-4 py-3 text-sm">{p.availability}</td>
                      <td className="px-4 py-3 text-sm">{p.featured ? "Yes" : "No"}</td>
                      <td className="px-4 py-3 flex gap-2">
                        <button onClick={() => openEdit(p)} className="text-xs text-forest hover:text-gold-600 underline">Edit</button>
                        <button onClick={() => deleteProduct(p.slug)} className="text-red-600"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editing && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setCreating(false); }}>
                <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-5">
                    <h3 className="font-serif text-2xl text-forest">{creating ? "Add Product" : "Edit Product"}</h3>
                    <button onClick={() => { setEditing(null); setCreating(false); }}><X size={20} /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Inp label="Slug" value={form.slug} disabled={!creating} onChange={(v) => setForm({ ...form, slug: v })} />
                    <Inp label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <Sel label="Category" value={form.category} options={CATEGORIES} onChange={(v) => setForm({ ...form, category: v })} />
                    <Sel label="Availability" value={form.availability} options={AVAILABILITY} onChange={(v) => setForm({ ...form, availability: v })} />
                    <Inp label="Short Description" full value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} />
                    <Inp label="Long Description" full textarea value={form.long_description} onChange={(v) => setForm({ ...form, long_description: v })} />
                    <Inp label="Origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} />
                    <Inp label="Variety" value={form.variety} onChange={(v) => setForm({ ...form, variety: v })} />
                    <Inp label="Packaging" full value={form.packaging} onChange={(v) => setForm({ ...form, packaging: v })} />
                    <ImageField
                      label="Product Image"
                      value={form.image_url}
                      onChange={(v) => setForm({ ...form, image_url: v })}
                    />
                    <Inp label="MOQ" value={form.moq} onChange={(v) => setForm({ ...form, moq: v })} />
                    <Inp label="Supply Capacity" value={form.supply_capacity} onChange={(v) => setForm({ ...form, supply_capacity: v })} />
                    <Inp label="Shipment Options" full value={form.shipment_options} onChange={(v) => setForm({ ...form, shipment_options: v })} />
                    <label className="flex items-center gap-2 sm:col-span-2 mt-2">
                      <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                      <span className="text-sm">Featured on homepage</span>
                    </label>
                  </div>
                  <div className="mt-6 flex gap-3 justify-end">
                    <button onClick={() => { setEditing(null); setCreating(false); }} className="px-5 py-2.5 text-sm border border-borderSubtle">Cancel</button>
                    <button onClick={saveProduct} className="btn-gold px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} className={`px-4 py-3 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px ${active ? "border-gold text-forest" : "border-transparent text-slateneutral"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}
function Inp({ label, value, onChange, full, textarea, disabled }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-widest font-semibold text-charcoal block mb-1.5">{label}</span>
      {textarea
        ? <textarea rows={3} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-borderSubtle focus:outline-none focus:border-forest disabled:bg-surface" />
        : <input disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-borderSubtle focus:outline-none focus:border-forest disabled:bg-surface" />}
    </label>
  );
}
function Sel({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-charcoal block mb-1.5">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-borderSubtle bg-white focus:outline-none focus:border-forest">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}

function ImageField({ label, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max image size is 8 MB."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const publicUrl = `${BACKEND_URL}/api/files/${data.path}`;
      onChange(publicUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <label className="block sm:col-span-2">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-charcoal block mb-1.5">{label}</span>
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div className="w-32 h-32 shrink-0 bg-surface border border-borderSubtle overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <ImageIcon size={28} className="text-slateneutral" />
          )}
        </div>
        <div className="flex-1 space-y-2 w-full">
          <input
            data-testid="admin-image-url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste image URL or upload below"
            className="w-full px-3 py-2 text-sm border border-borderSubtle focus:outline-none focus:border-forest"
          />
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="admin-image-file" />
          <button
            type="button"
            data-testid="admin-image-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-forest text-forest hover:bg-forest hover:text-white transition disabled:opacity-60"
          >
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
          </button>
          <p className="text-[11px] text-slateneutral">JPG, PNG, WEBP or GIF · Max 8 MB</p>
        </div>
      </div>
    </label>
  );
}
